// ConfirmModal
// -------------
// Reusable confirmation dialog. Replaces the browser's blocking window.confirm()
// with a modal that matches the rest of the dashboard's visual language.
//
// Usage:
//   <ConfirmModal
//     show={this.state.confirmDeleteOpen}
//     title="Delete this credential?"
//     message="Any integrations using it will stop working."
//     confirmLabel="Delete"
//     destructive
//     onConfirm={this.handleDeleteConfirmed}
//     onCancel={() => this.setState({ confirmDeleteOpen: false })}
//   />

import React, { useRef } from "react";
import ReactDOM from "react-dom";
import { AlertTriangle, X } from "lucide-react";
import { useModalAccessibility } from "@/utils/useModalAccessibility";

interface ConfirmModalProps {
  show: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  // When true, the Cancel button is hidden — turning the dialog into an
  // acknowledge-only info modal. The Confirm button becomes the dismiss action.
  hideCancel?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmModal({
  show,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  hideCancel = false,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  // Escape closes; focus shifts into the modal on open and returns to
  // the trigger on close.
  const dialogContainerRef = useRef<HTMLDivElement>(null);
  useModalAccessibility(show, onCancel, dialogContainerRef);

  if (!show) return null;

  const confirmButtonClass = destructive
    ? "inline-flex items-center justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 transition-colors cursor-pointer"
    : "inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 transition-colors cursor-pointer";

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" />

      {/* Modal */}
      <div
        ref={dialogContainerRef}
        className="relative w-full max-w-md rounded-xl bg-white shadow-xl"
        onClick={(clickEvent) => clickEvent.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            {destructive && (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-50">
                <AlertTriangle className="h-4 w-4 text-red-600" />
              </div>
            )}
            <h2 id="confirm-modal-title" className="text-base font-semibold text-gray-900">
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <div className="text-sm text-gray-600">{message}</div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50/60 rounded-b-xl">
          {!hideCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              {cancelLabel}
            </button>
          )}
          <button type="button" onClick={onConfirm} className={confirmButtonClass}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
